/**
 * R138u · Feb 2026 — Portfolio Board Notes tab.
 * R138w · Body supports `@task:<id>` mentions rendered as chips.
 * R138x · Rich text (SmartRichText) + `@` task-picker autocomplete.
 *
 * Notes are logged at the portfolio level with 5 categories
 * (Decision · Action · Highlight · Concern · Note). Pinned notes float to
 * the top so directors always see the critical ones first.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";
import { fmtRelative } from "@/lib/format";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  Pin, PinOff, Sparkles, CheckSquare, AlertTriangle, MessageSquare,
  Gavel, Trash2, Edit3, Save, X, Plus, Hash,
} from "lucide-react";
import SmartRichText from "@/components/SmartRichText";
import { PortfolioTaskEditDialog } from "@/components/portfolio/PortfolioCombinedViews";

const CATEGORY_META = {
  decision:  { label: "Decision",  Icon: Gavel,          color: "text-violet-800 bg-violet-50 border-violet-300" },
  action:    { label: "Action",    Icon: CheckSquare,    color: "text-sky-800 bg-sky-50 border-sky-300" },
  highlight: { label: "Highlight", Icon: Sparkles,       color: "text-emerald-800 bg-emerald-50 border-emerald-300" },
  concern:   { label: "Concern",   Icon: AlertTriangle,  color: "text-rose-800 bg-rose-50 border-rose-300" },
  note:      { label: "Note",      Icon: MessageSquare,  color: "text-slate-800 bg-slate-50 border-slate-300" },
};

export default function BoardNotesTab({ portfolioId }) {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showComposer, setShowComposer] = useState(false);
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [editingId, setEditingId] = useState(null);
  const [openTaskId, setOpenTaskId] = useState(null);
  const [taskIndex, setTaskIndex] = useState([]); // for id → title resolution

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/portfolios/${portfolioId}/board-notes`);
      setNotes(data.items || []);
    } catch (e) { toast.error(apiErrorMessage(e, "Failed to load notes")); }
    finally { setLoading(false); }
  }, [portfolioId]);

  // R138x · Load portfolio task index once so mention chips can render
  // task titles instead of raw UUIDs.
  useEffect(() => {
    api.get(`/portfolios/${portfolioId}/tasks-index?limit=100`)
      .then((r) => setTaskIndex(r.data.items || []))
      .catch(() => setTaskIndex([]));
  }, [portfolioId]);

  useEffect(() => { load(); }, [load]);

  const togglePin = async (n) => {
    try {
      await api.patch(`/portfolios/board-notes/${n.id}`, { is_pinned: !n.is_pinned });
      toast.success(!n.is_pinned ? "Pinned" : "Unpinned");
      load();
    } catch (e) { toast.error(apiErrorMessage(e, "Update failed")); }
  };

  const removeNote = async (n) => {
    if (!window.confirm(`Delete this ${(CATEGORY_META[n.category]?.label || "note").toLowerCase()}?`)) return;
    try {
      await api.delete(`/portfolios/board-notes/${n.id}`);
      toast.success("Note deleted");
      load();
    } catch (e) { toast.error(apiErrorMessage(e, "Delete failed")); }
  };

  const filtered = categoryFilter === "all"
    ? notes
    : notes.filter((n) => n.category === categoryFilter);

  const counts = notes.reduce((acc, n) => {
    acc[n.category] = (acc[n.category] || 0) + 1;
    return acc;
  }, {});

  const taskTitleById = useMemo(() => {
    const m = {};
    for (const t of taskIndex) m[t.id] = t.title;
    return m;
  }, [taskIndex]);

  return (
    <div className="space-y-4" data-testid="portfolio-board-notes-tab">
      {/* Filter chips + new note */}
      <div className="flex items-center gap-2 flex-wrap">
        <button
          type="button"
          onClick={() => setCategoryFilter("all")}
          className={`text-[11px] uppercase tracking-widest px-2 py-1 border ${categoryFilter === "all" ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-muted/40"}`}
          data-testid="board-notes-filter-all"
        >
          All · {notes.length}
        </button>
        {Object.entries(CATEGORY_META).map(([key, meta]) => {
          const Icon = meta.Icon;
          const active = categoryFilter === key;
          const count = counts[key] || 0;
          return (
            <button
              key={key}
              type="button"
              onClick={() => setCategoryFilter(key)}
              className={`text-[11px] uppercase tracking-widest px-2 py-1 border inline-flex items-center gap-1 ${active ? meta.color + " font-semibold" : "border-border hover:bg-muted/40"}`}
              data-testid={`board-notes-filter-${key}`}
            >
              <Icon className="w-3 h-3" /> {meta.label} · {count}
            </button>
          );
        })}
        <div className="flex-1" />
        <Button size="sm" className="rounded-none"
                  onClick={() => setShowComposer((v) => !v)}
                  data-testid="board-notes-new-btn">
          <Plus className="w-3.5 h-3.5 mr-1.5" />
          {showComposer ? "Close composer" : "New note"}
        </Button>
      </div>

      {/* Composer */}
      {showComposer && (
        <NoteComposer portfolioId={portfolioId}
                             onSaved={() => { setShowComposer(false); load(); }}
                             onCancel={() => setShowComposer(false)} />
      )}

      {/* Notes grid */}
      {loading ? (
        <div className="text-sm text-muted-foreground p-6 text-center">Loading notes…</div>
      ) : filtered.length === 0 ? (
        <div className="border border-dashed border-border p-10 text-center text-sm text-muted-foreground">
          {categoryFilter === "all" ? "No board notes yet." : `No ${CATEGORY_META[categoryFilter]?.label.toLowerCase()} notes yet.`} Click <b>New note</b> to add one.
        </div>
      ) : (
        <div className="grid gap-3 md:grid-cols-2">
          {filtered.map((n) => (
            editingId === n.id ? (
              <NoteComposer key={n.id}
                                    portfolioId={portfolioId}
                                    note={n}
                                    onSaved={() => { setEditingId(null); load(); }}
                                    onCancel={() => setEditingId(null)} />
            ) : (
              <NoteCard key={n.id} note={n}
                             taskTitleById={taskTitleById}
                             onTogglePin={() => togglePin(n)}
                             onEdit={() => setEditingId(n.id)}
                             onDelete={() => removeNote(n)}
                             onTaskClick={(taskId) => setOpenTaskId(taskId)} />
            )
          ))}
        </div>
      )}
      {openTaskId && (
        <PortfolioTaskEditDialog
          taskId={openTaskId}
          projectName={"Task from board note"}
          onClose={() => setOpenTaskId(null)}
        />
      )}
    </div>
  );
}


// R138x · Feb 2026 — Rich-text-aware note card.
//
// A note body is now HTML. We first extract every `@task:<uuid>` token
// (produced by SmartRichText's @-picker) and swap it for a live chip
// that opens the full Task Edit dialog on click. The rest of the HTML
// (bold / italic / lists / links) is rendered via dangerouslySetInnerHTML
// through a tiny stateless component that also delegates click events
// to catch chip clicks.
function NoteCard({ note, taskTitleById, onTogglePin, onEdit, onDelete, onTaskClick }) {
  const meta = CATEGORY_META[note.category] || CATEGORY_META.note;
  const Icon = meta.Icon;
  return (
    <div className={`border p-4 relative ${note.is_pinned ? "border-primary bg-primary/5" : "border-border bg-card"}`}
             data-testid={`board-note-card-${note.id}`}>
      <div className="flex items-start justify-between gap-2 mb-2">
        <span className={`text-[10px] uppercase tracking-widest px-1.5 py-0.5 border inline-flex items-center gap-1 ${meta.color}`}>
          <Icon className="w-3 h-3" /> {meta.label}
        </span>
        <div className="flex items-center gap-0.5">
          <button type="button" onClick={onTogglePin}
                    className={`p-1 hover:bg-muted rounded-none ${note.is_pinned ? "text-primary" : "text-muted-foreground"}`}
                    title={note.is_pinned ? "Unpin" : "Pin to top"}
                    data-testid={`board-note-pin-${note.id}`}>
            {note.is_pinned ? <Pin className="w-3.5 h-3.5 fill-current" /> : <PinOff className="w-3.5 h-3.5" />}
          </button>
          <button type="button" onClick={onEdit}
                    className="p-1 hover:bg-muted text-muted-foreground"
                    title="Edit"
                    data-testid={`board-note-edit-${note.id}`}>
            <Edit3 className="w-3.5 h-3.5" />
          </button>
          <button type="button" onClick={onDelete}
                    className="p-1 hover:bg-rose-50 text-rose-600"
                    title="Delete"
                    data-testid={`board-note-delete-${note.id}`}>
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
      <div className="font-semibold text-sm mb-1" data-testid={`board-note-title-${note.id}`}>{note.title}</div>
      {note.body && (
        <NoteBodyRenderer body={note.body}
                                    taskTitleById={taskTitleById}
                                    onTaskClick={onTaskClick}
                                    testid={`board-note-body-${note.id}`} />
      )}
      <div className="text-[10px] text-muted-foreground mt-3 flex items-center gap-2">
        <span>{note.author_name || "—"}</span>
        <span>·</span>
        <span>{fmtRelative(note.created_at)}</span>
        {note.updated_at && note.updated_at !== note.created_at && (
          <><span>·</span><span className="italic">edited {fmtRelative(note.updated_at)}</span></>
        )}
      </div>
    </div>
  );
}


// R138x · Swap every `@task:<uuid>` occurrence with an interactive chip
// (inside HTML text nodes only — leaves markup intact). Uses a stateless
// DOM traversal via a template element to keep XSS surface identical to
// plain dangerouslySetInnerHTML.
const TASK_TOKEN = /@task:([0-9a-fA-F-]{8,})/g;
function NoteBodyRenderer({ body, taskTitleById, onTaskClick, testid }) {
  // Fast path: if body has no mention tokens, render directly.
  const hasToken = body && TASK_TOKEN.test(body);
  TASK_TOKEN.lastIndex = 0; // reset regex state after `test`
  // If body still looks like plain-text (legacy notes), wrap in <p> so
  // whitespace + newlines survive. Otherwise trust the HTML.
  const looksHtml = /<[a-z][^>]*>/i.test(body || "");
  const html = looksHtml
    ? (body || "")
    : `<p style="white-space:pre-wrap">${(body || "").replace(/&/g, "&amp;").replace(/</g, "&lt;")}</p>`;
  if (!hasToken) {
    return (
      <div className="prose prose-sm max-w-none text-xs text-muted-foreground leading-relaxed [&_p]:my-1 [&_ul]:my-1 [&_ol]:my-1"
             dangerouslySetInnerHTML={{ __html: html }}
             data-testid={testid} />
    );
  }
  // Interactive path: split HTML into text fragments and mention chips.
  // We split conservatively on the token regex only inside text — since
  // the token is plain ASCII it can't validly appear inside a tag name.
  // Any `@task:` inside an attribute would still be caught but never
  // typed by a user in practice.
  const parts = html.split(TASK_TOKEN);
  // Even indices = raw HTML text · Odd indices = captured uuid
  return (
    <div className="prose prose-sm max-w-none text-xs text-muted-foreground leading-relaxed [&_p]:my-1 [&_ul]:my-1 [&_ol]:my-1"
             data-testid={testid}>
      {parts.map((chunk, i) => {
        if (i % 2 === 1) {
          const id = chunk;
          const title = taskTitleById?.[id];
          return (
            <button key={`chip-${i}`} type="button"
                       onClick={() => onTaskClick?.(id)}
                       className="inline-flex items-center gap-0.5 text-[11px] px-1 py-0 mx-0.5 border border-primary/40 text-primary bg-primary/5 hover:bg-primary/10 cursor-pointer font-medium align-baseline"
                       title="Open the full task editor"
                       data-testid={`board-note-task-mention-${id}`}>
              <Hash className="w-2.5 h-2.5" />{title || `${id.slice(0, 6)}…`}
            </button>
          );
        }
        return (
          <span key={`html-${i}`} dangerouslySetInnerHTML={{ __html: chunk }} />
        );
      })}
    </div>
  );
}


function NoteComposer({ portfolioId, note, onSaved, onCancel }) {
  const { activeWorkspaceId } = useAuth();
  const editing = !!note;
  const [category, setCategory] = useState(note?.category || "note");
  const [title, setTitle] = useState(note?.title || "");
  const [body, setBody] = useState(note?.body || "");
  const [isPinned, setIsPinned] = useState(!!note?.is_pinned);
  const [busy, setBusy] = useState(false);
  const [taskIndex, setTaskIndex] = useState([]);
  // Load a fresh task index for the picker.
  useEffect(() => {
    api.get(`/portfolios/${portfolioId}/tasks-index?limit=100`)
      .then((r) => setTaskIndex(r.data.items || []))
      .catch(() => setTaskIndex([]));
  }, [portfolioId]);

  // Feed SmartRichText — `insert` is the machine marker, `display` is
  // the human-readable label + a subtle project subtitle.
  const mentionSuggestions = useMemo(() => taskIndex.map((t) => ({
    id: t.id,
    label: t.title || t.id,
    display: t.title || t.id,
    sub: t.project_name || "",
    insert: `task:${t.id}`,
  })), [taskIndex]);

  const submit = async () => {
    if (!title.trim()) { toast.error("Title is required"); return; }
    setBusy(true);
    try {
      const payload = { category, title: title.trim(), body: body || "", is_pinned: isPinned };
      if (editing) {
        await api.patch(`/portfolios/board-notes/${note.id}`, payload);
        toast.success("Note updated");
      } else {
        await api.post(`/portfolios/${portfolioId}/board-notes`, payload);
        toast.success("Note added");
      }
      onSaved?.();
    } catch (e) { toast.error(apiErrorMessage(e, "Save failed")); }
    finally { setBusy(false); }
  };
  return (
    <div className="border border-primary p-4 bg-primary/5 space-y-3"
             data-testid={editing ? `board-note-composer-edit-${note.id}` : "board-note-composer"}>
      <div className="flex items-center gap-2 flex-wrap">
        <Select value={category} onValueChange={setCategory}>
          <SelectTrigger className="h-8 w-40 rounded-none text-xs" data-testid="board-note-category">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {Object.entries(CATEGORY_META).map(([k, m]) => (
              <SelectItem key={k} value={k}>{m.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <label className="text-xs flex items-center gap-1.5 cursor-pointer">
          <input type="checkbox" checked={isPinned} onChange={(e) => setIsPinned(e.target.checked)}
                     className="h-3.5 w-3.5 accent-primary"
                     data-testid="board-note-pin-toggle" />
          <Pin className="w-3 h-3" /> Pin to top
        </label>
        <span className="ml-auto text-[10px] text-muted-foreground italic">
          Type <span className="font-mono border px-1">@</span> to mention a task
        </span>
      </div>
      <Input value={title} onChange={(e) => setTitle(e.target.value)}
                 placeholder="Note title (e.g. Board approved additional headcount for Q2)"
                 className="rounded-none text-sm"
                 data-testid="board-note-title-input" />
      <SmartRichText
        value={body}
        onChange={setBody}
        placeholder="Details, context, owners, dates…"
        workspaceId={activeWorkspaceId}
        mediaMode="off"
        minRows={3}
        maxHeight={320}
        allowAiImprove={false}
        mentionSuggestions={mentionSuggestions}
        testid="board-note-body-rich"
      />
      <div className="flex items-center justify-end gap-2">
        <Button size="sm" variant="outline" className="rounded-none"
                  onClick={onCancel} data-testid="board-note-cancel">
          <X className="w-3.5 h-3.5 mr-1.5" /> Cancel
        </Button>
        <Button size="sm" className="rounded-none"
                  onClick={submit} disabled={busy}
                  data-testid="board-note-save">
          <Save className="w-3.5 h-3.5 mr-1.5" />
          {busy ? "Saving…" : (editing ? "Save changes" : "Add note")}
        </Button>
      </div>
    </div>
  );
}
